// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useCallback, useEffect, useRef, useState } from 'react';
import { Platform } from 'react-native';
import * as Linking from 'expo-linking';
import * as WebBrowser from 'expo-web-browser';
import { useRouter, useSegments } from 'expo-router';

import { buildAuthUrl, exchangeCode, parseAuthCallback, REDIRECT_URI } from '@/auth/oidc';
import type { UserSession } from '@/app/user-context';

type UseAuthFlowOptions = {
  user: UserSession | null;
  setUser: (user: UserSession | null) => void;
};

type AuthExchangeState = { code: string | null; inFlight: boolean };

WebBrowser.maybeCompleteAuthSession();

export const useAuthFlow = ({ user, setUser }: UseAuthFlowOptions) => {
  const router = useRouter();
  const segments = useSegments();
  const [authChecked, setAuthChecked] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const authExchangeRef = useRef<AuthExchangeState>({ code: null, inFlight: false });

  const handleAuthCallbackUrl = useCallback(
    async (url: string) => {
      const { code, state } = parseAuthCallback(url);
      if (!code) return;
      if (authExchangeRef.current.inFlight || authExchangeRef.current.code === code) {
        return;
      }
      authExchangeRef.current = { code, inFlight: true };
      try {
        const auth = await exchangeCode(code, state ?? undefined);
        setUser({
          id: auth.id,
          forename: auth.name,
          token: auth.accessToken,
          idToken: auth.idToken,
          refreshToken: auth.refreshToken,
        });
        router.replace('/tournaments');
      } catch (err) {
        console.error('Failed to complete auth callback', err);
        authExchangeRef.current = { code, inFlight: false };
      }
    },
    [router, setUser]
  );

  useEffect(() => {
    setAuthChecked(true);
  }, []);

  useEffect(() => {
    if (!authChecked) return;
    const first = segments[0];
    const inAuthFlow = first === 'login' || first === 'register' || first === 'callback';
    if (!user && !inAuthFlow && !redirecting) {
      (async () => {
        try {
          setRedirecting(true);
          const url = await buildAuthUrl();
          if (Platform.OS === 'web' && typeof window !== 'undefined' && window.location) {
            window.location.href = url;
            return;
          }

          const canUseAuthSession = typeof WebBrowser.isAvailableAsync === 'function'
            ? await WebBrowser.isAvailableAsync().catch(() => false)
            : false;
          if (!canUseAuthSession) {
            await Linking.openURL(url);
            setRedirecting(false);
            return;
          }
          try {
            const result = await WebBrowser.openAuthSessionAsync(url, REDIRECT_URI);
            console.log('layout auth session result', result.type, result.url ?? '');
            if (result.type === 'success' && result.url) {
              const { code, state } = parseAuthCallback(result.url);
              if (!code) {
                setRedirecting(false);
                return;
              }
              const auth = await exchangeCode(code, state ?? undefined);
              setUser({
                id: auth.id,
                forename: auth.name,
                token: auth.accessToken,
                idToken: auth.idToken,
                refreshToken: auth.refreshToken,
              });
              router.replace('/tournaments');
              return;
            }
          } catch (authErr) {
            console.error('Auth session failed, opening browser', authErr);
            await Linking.openURL(url);
          }

          setRedirecting(false);
        } catch (err) {
          console.error('Failed to start auth flow', err);
          setRedirecting(false);
        }
      })();
    } else if (user && inAuthFlow) {
      router.replace('/tournaments');
    }
  }, [authChecked, user, segments, router, redirecting, setUser]);

  useEffect(() => {
    if (Platform.OS === 'web') return;
    const handler = ({ url }: { url: string }) => {
      console.log('deep link received', url);
      handleAuthCallbackUrl(url);
    };
    const subscription = Linking.addEventListener('url', handler);
    Linking.getInitialURL()
      .then((url) => {
        if (url) {
          console.log('deep link initial', url);
          handleAuthCallbackUrl(url);
        }
      })
      .catch(() => null);
    return () => subscription.remove();
  }, [handleAuthCallbackUrl]);

  return { authChecked };
};
